import { useState, useEffect } from 'react'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import {
  EnvelopeSimple,
  Buildings,
  Clock,
  CheckCircle,
  XCircle,
  Spinner,
  Warning,
  Handshake,
} from '@phosphor-icons/react'
import {
  getFFInvites,
  respondToInvite,
  FFInvite,
} from '../../services/ff-invites'
import { useSidebarBadges } from '../../context/SidebarBadgeContext'
import { formatDateBR, daysUntil } from '../../lib/date'

export default function InvitesPage() {
  const { showError } = useDialog()
  const [isLoading, setIsLoading] = useState(true)
  const [invites, setInvites] = useState<FFInvite[]>([])
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [declineModal, setDeclineModal] = useState<string | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const { refreshBadges } = useSidebarBadges()

  // Load invites
  const loadInvites = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getFFInvites()
      setInvites(data)
    } catch (err: any) {
      console.error('Error loading invites:', err)
      setError(err.response?.data?.error || 'Erro ao carregar convites')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadInvites()
  }, [])

  // Handle accept invite
  const handleAccept = async (inviteId: string) => {
    setActionLoading(inviteId)
    try {
      await respondToInvite(inviteId, { accept: true })
      setInvites((prev) => prev.filter((i) => i.id !== inviteId))
      refreshBadges()
    } catch (err: any) {
      console.error('Error accepting invite:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao aceitar convite')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle decline invite
  const handleDecline = async (inviteId: string) => {
    setActionLoading(inviteId)
    try {
      await respondToInvite(inviteId, {
        accept: false,
        declineReason: declineReason || undefined,
      })
      setInvites((prev) => prev.filter((i) => i.id !== inviteId))
      setDeclineModal(null)
      setDeclineReason('')
      refreshBadges()
    } catch (err: any) {
      console.error('Error declining invite:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao recusar convite')
    } finally {
      setActionLoading(null)
    }
  }

  // Calculate days until expiration
  const daysUntilExpiration = (expiresAt: string) => {
    return daysUntil(expiresAt)
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <EnvelopeSimple size={28} className="text-emerald-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Convites de Parceria</h1>
                <p className="text-sm text-gray-500">
                  Gerencie os convites recebidos de embarcadores
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <Warning size={24} className="text-red-500" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadInvites}
              className="ml-auto text-red-600 hover:text-red-800 underline text-sm"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={40} className="animate-spin text-emerald-600" />
          </div>
        ) : invites.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Handshake size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">Nenhum convite pendente</p>
              <p className="text-sm text-gray-400 mt-1">
                Quando um embarcador enviar um convite de parceria, ele aparecera aqui
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {invites.map((invite) => {
              const daysLeft = daysUntilExpiration(invite.expiresAt)
              const isExpiringSoon = daysLeft <= 7

              return (
                <Card key={invite.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                          <Buildings size={28} className="text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {invite.shipperName}
                          </h3>
                          {invite.shipperTradingName && (
                            <p className="text-gray-500">{invite.shipperTradingName}</p>
                          )}
                          {invite.shipperDocument && (
                            <p className="text-sm text-gray-400">
                              CNPJ: {invite.shipperDocument}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-3">
                            <span className="flex items-center gap-1 text-sm text-gray-500">
                              <Clock size={16} />
                              Recebido em: {formatDateBR(invite.createdAt)}
                            </span>
                            <span
                              className={`flex items-center gap-1 text-sm ${
                                isExpiringSoon ? 'text-amber-600' : 'text-gray-500'
                              }`}
                            >
                              <Clock size={16} />
                              {isExpiringSoon
                                ? `Expira em ${daysLeft} dia${daysLeft !== 1 ? 's' : ''}`
                                : `Expira em: ${formatDateBR(invite.expiresAt)}`}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3">
                        {actionLoading === invite.id ? (
                          <Spinner size={24} className="animate-spin text-emerald-600" />
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => setDeclineModal(invite.id)}
                              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <XCircle size={20} />
                              Recusar
                            </Button>
                            <Button
                              onClick={() => handleAccept(invite.id)}
                              className="gap-2"
                              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                            >
                              <CheckCircle size={20} />
                              Aceitar
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Info box */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700">
                        Ao aceitar este convite, voce podera receber solicitacoes de cotacoes
                        de frete deste embarcador e enviar propostas comerciais.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </main>

      {/* Decline Modal */}
      {declineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setDeclineModal(null)
              setDeclineReason('')
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recusar Convite</h3>
            <p className="text-gray-500 text-sm mb-4">
              Tem certeza que deseja recusar este convite de parceria?
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo (opcional)
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Informe o motivo da recusa..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setDeclineModal(null)
                  setDeclineReason('')
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleDecline(declineModal)}
                disabled={actionLoading === declineModal}
                className="gap-2 bg-red-600 hover:bg-red-700"
              >
                {actionLoading === declineModal ? (
                  <Spinner size={20} className="animate-spin" />
                ) : (
                  <XCircle size={20} />
                )}
                Confirmar Recusa
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
